import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search,
  Plus,
  Building,
  Users,
  MoreHorizontal,
  Ban,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/hooks/use-auth";
import {
  useAdminWorkspaces,
  useSuspendWorkspace,
  useReactivateWorkspace,
  type AdminWorkspace,
} from "@/hooks/use-admin-workspaces";
import { CreateCustomerUserDialog } from "@/components/admin/CreateCustomerUserDialog";

function WorkspaceSkeleton() {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
      <div className="flex items-center gap-4">
        <Skeleton className="w-10 h-10 rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Skeleton className="h-4 w-10" />
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-16 rounded-full" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
    </div>
  );
}

interface WorkspaceRowProps {
  ws: AdminWorkspace;
  isSuperAdmin: boolean;
}

function WorkspaceRow({ ws, isSuperAdmin }: WorkspaceRowProps) {
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const suspendMutation = useSuspendWorkspace();
  const reactivateMutation = useReactivateWorkspace();

  const isPending =
    suspendMutation.isPending || reactivateMutation.isPending;

  return (
    <>
      <div className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-accent/50 transition-colors">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Building className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium">{ws.name}</p>
            <p className="text-xs text-muted-foreground">
              {ws.propertyCount} {ws.propertyCount === 1 ? "property" : "properties"} ·{" "}
              created {formatDistanceToNow(new Date(ws.created_at), { addSuffix: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            {ws.memberCount}
          </div>
          {ws.plan && (
            <Badge variant={ws.plan === "pro" ? "default" : "secondary"}>
              {ws.plan}
            </Badge>
          )}
          <Badge variant={ws.status === "active" ? "outline" : "destructive"}>
            {ws.status ?? "active"}
          </Badge>
          {isSuperAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" disabled={isPending}>
                  {isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MoreHorizontal className="w-4 h-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {ws.status !== "suspended" ? (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setSuspendOpen(true)}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Suspend
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => setReactivateOpen(true)}>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Reactivate
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <AlertDialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              Members of <strong>{ws.name}</strong> will lose access immediately.
              This can be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => suspendMutation.mutate(ws.id)}
            >
              Suspend
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{ws.name}</strong> will be set back to active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => reactivateMutation.mutate(ws.id)}
            >
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function AdminWorkspaces() {
  const { syntraAdminRole } = useAuth();
  const isSuperAdmin = syntraAdminRole === "super_admin";
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: workspaces, isLoading, error } = useAdminWorkspaces();

  const filtered = (workspaces ?? []).filter((ws) =>
    ws.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <CreateCustomerUserDialog
        open={createUserOpen}
        onOpenChange={setCreateUserOpen}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Workspaces</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {isLoading
              ? "Loading..."
              : `${workspaces?.length ?? 0} workspace${workspaces?.length === 1 ? "" : "s"}`}
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            className="gradient-primary"
            onClick={() => setCreateUserOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Workspace
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search workspaces..."
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle>All Workspaces</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <WorkspaceSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-4 text-center">
              Failed to load workspaces.
            </p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              {search ? "No workspaces match your search." : "No workspaces yet."}
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map((ws) => (
                <WorkspaceRow
                  key={ws.id}
                  ws={ws}
                  isSuperAdmin={isSuperAdmin}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
